import { useQuery } from 'react-query';
import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';
import { OrganizationAccreditationConfig } from '../../../_types/accreditation';

export async function getOrganizationAccreditationConfig(
  orgId: string,
  signal?: AbortSignal
): Promise<OrganizationAccreditationConfig> {
  if (!orgId) throw new Error('No organization ID provided.');

  const url = `${baseApiUrl()}/admin-solution/v1/organizations/${orgId}/solutions/awb/config`;

  try {
    const response = await fetcher({
      method: 'GET',
      url,
      options: { signal },
    });
    return response;
  } catch (error: unknown) {
    if (typeof error === 'object' && error !== null && 'errorDetails' in error) {
      throw error as ServerErrorResponse;
    }
    throw error;
  }
}

export default function useOrganizationAccreditationConfig(
  orgId: string,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  return useQuery<OrganizationAccreditationConfig, ServerErrorResponse>(
    ['organizationAccreditationConfig', orgId],
    async ({ signal }): Promise<OrganizationAccreditationConfig> => {
      try {
        return await getOrganizationAccreditationConfig(orgId, signal);
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      enabled: !!orgId,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      ...options,
      keepPreviousData: true,
    }
  );
}
